import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { dataService } from '@/services/dataService';
import { Contract, Partnership } from '@/types/entities';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { ConfirmModal } from '@/components/ui/ConfirmModal';
import { exportRowsToCsv } from '@/lib/exportCsv';
import { useI18n } from '@/i18n';
import { useToast } from '@/components/ui/Toast';
import { ContractFormDrawer, ContractFormValues } from './ContractFormDrawer';

export function ContractsPage() {
  const { t } = useI18n();
  const push = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [partnerships, setPartnerships] = useState<Partnership[]>([]);
  const [formOpen, setFormOpen] = useState(!!searchParams.get('new'));
  const [editTarget, setEditTarget] = useState<Contract | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<Contract | null>(null);

  function reload() {
    dataService.listContracts().then(setContracts);
    dataService.listPartnerships().then(setPartnerships);
  }
  useEffect(reload, []);

  async function handleCreate(values: ContractFormValues) {
    const created = await dataService.createContract(values);
    push(`${t('toast.contractCreated')} — ${created.id}`, 'success');
    setFormOpen(false);
    setSearchParams({});
    reload();
  }
  async function handleEdit(values: ContractFormValues) {
    if (!editTarget) return;
    await dataService.updateContract(editTarget.id, values);
    push(t('toast.contractUpdated'), 'success');
    setEditTarget(null);
    reload();
  }
  async function duplicate(c: Contract) {
    const copy = await dataService.duplicateContractVersion(c.id);
    if (copy) push(`${t('toast.versionDuplicated')} — ${copy.id} ${copy.version}`, 'success');
    reload();
  }
  async function confirmArchive() {
    if (!archiveTarget) return;
    await dataService.archiveContract(archiveTarget.id);
    push(t('toast.contractArchived'), 'warn');
    setArchiveTarget(null);
    reload();
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1>{t('nav.contracts')}</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => {
            exportRowsToCsv('contracts', contracts, [
              { key: 'id', header: 'ID', value: (c) => c.id },
              { key: 'title', header: 'Title', value: (c) => c.title },
              { key: 'status', header: 'Status', value: (c) => c.status },
            ]);
            dataService.logExport('Contracts');
          }}>{t('common.export')}</button>
          <button onClick={() => window.print()}>{t('common.print')}</button>
          <button className="btn-primary" onClick={() => setFormOpen(true)}>+ {t('contractForm.newContract')}</button>
        </div>
      </div>

      <div className="card">
        {contracts.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center' }}>
            <div style={{ color: 'var(--text-3)', marginBottom: 12 }}>{t('empty.noContracts')}</div>
            <button className="btn-primary" onClick={() => setFormOpen(true)}>+ {t('contractForm.newContract')}</button>
          </div>
        ) : (
          <div className="table-scroll">
          <table className="dt">
            <thead><tr><th>ID</th><th>Title</th><th>Version</th><th>Status</th><th>Expiry</th><th></th></tr></thead>
            <tbody>
              {contracts.map((c) => (
                <tr key={c.id}>
                  <td>{c.id}</td>
                  <td>{c.title}</td>
                  <td>{c.version}</td>
                  <td><StatusBadge entity="contract" statusKey={c.status} /></td>
                  <td>{c.expiryDate ?? '—'}</td>
                  <td style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => setEditTarget(c)}>{t('rowMenu.edit')}</button>
                    <button onClick={() => duplicate(c)}>{t('contractForm.duplicateVersion')}</button>
                    <button style={{ color: 'var(--warn)' }} onClick={() => setArchiveTarget(c)}>{t('rowMenu.archive')}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {formOpen && (
        <ContractFormDrawer
          partnerships={partnerships}
          defaultPartnershipId={searchParams.get('new') ?? undefined}
          onClose={() => { setFormOpen(false); setSearchParams({}); }}
          onSubmit={handleCreate}
        />
      )}
      {editTarget && (
        <ContractFormDrawer contract={editTarget} partnerships={partnerships} onClose={() => setEditTarget(null)} onSubmit={handleEdit} />
      )}

      <ConfirmModal
        open={!!archiveTarget}
        title={t('confirm.archiveContractTitle')}
        recordName={archiveTarget ? `${archiveTarget.id} — ${archiveTarget.title}` : undefined}
        description={t('confirm.archiveContractDesc')}
        reversible
        confirmLabel={t('rowMenu.archive')}
        onConfirm={confirmArchive}
        onCancel={() => setArchiveTarget(null)}
      />
    </div>
  );
}
